import React, { useEffect, useState, useId } from 'react';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { useReveal } from '@/lib/motion';

// Email tokens render as brand-orange mailto: links inside answer text.
const EMAIL_REGEX = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

// Bulleted answers: lines beginning with "- " convert to <ul><li>.
function parseAnswer(answer) {
  if (typeof answer !== 'string') return { kind: 'paragraph', text: '' };
  const lines = answer.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  const bulletLines = lines.filter((l) => l.startsWith('- '));
  if (bulletLines.length >= 2 && bulletLines.length === lines.length) {
    return { kind: 'list', items: bulletLines.map((l) => l.replace(/^-\s+/, '')) };
  }
  return { kind: 'paragraph', text: answer };
}

// Render a string with email tokens converted into mailto: links.
function renderWithEmails(text, keyPrefix = 'em') {
  const parts = [];
  let lastIndex = 0;
  let match;
  EMAIL_REGEX.lastIndex = 0;
  while ((match = EMAIL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const email = match[1];
    parts.push(
      <a
        key={`${keyPrefix}-${match.index}`}
        href={`mailto:${email}`}
        className="text-brand-orange no-underline hover:underline focus-visible:underline transition-colors"
        data-testid="faq-mailto-link"
      >
        {email}
      </a>,
    );
    lastIndex = match.index + email.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
}

function FAQRow({ entry, index, isOpen, onToggle }) {
  const panelId = useId();
  const buttonId = useId();
  const parsed = parseAnswer(entry.answer);

  const onKeyDown = (e) => {
    // Native <button> handles Enter+Space, but provide explicit handling for
    // assistive tech edge cases that may dispatch keydown without click.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(index);
    }
  };

  return (
    <div
      className="border-b border-ink-primary/15"
      data-testid="faq-row"
    >
      <button
        id={buttonId}
        type="button"
        onClick={() => onToggle(index)}
        onKeyDown={onKeyDown}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={`group w-full flex items-center justify-between gap-4 text-left py-5 md:py-6 px-1 cursor-pointer
          motion-safe:transition-colors motion-safe:duration-200 ease-out
          hover:bg-ink-primary/[0.03]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas
          rounded-sm`}
        data-testid="faq-row-trigger"
      >
        <span
          className={`font-display font-medium uppercase tracking-wide text-[16px] md:text-[19px] lg:text-[20px] leading-snug
            motion-safe:transition-colors motion-safe:duration-200
            ${isOpen ? 'text-brand-orange' : 'text-ink-primary'}`}
          data-testid="faq-row-question"
        >
          {entry.question}
        </span>
        <span
          className={`relative shrink-0 rounded-full border-2 border-brand-orange
            w-9 h-9 md:w-10 md:h-10 flex items-center justify-center
            motion-safe:transition-all motion-safe:duration-300 ease-out
            group-hover:motion-safe:scale-105
            ${isOpen ? 'bg-brand-orange rotate-45' : 'bg-transparent rotate-0'}`}
          aria-hidden="true"
        >
          <Plus
            size={18}
            strokeWidth={2.5}
            className={`motion-safe:transition-colors motion-safe:duration-200
              ${isOpen ? 'text-white' : 'text-brand-orange'}`}
          />
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`grid motion-safe:transition-[grid-template-rows,opacity] motion-safe:duration-300 ease-out
          ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        data-testid="faq-row-panel"
      >
        <div className="overflow-hidden">
          <div className="pb-6 md:pb-7 pr-2 max-w-[720px]">
            {parsed.kind === 'list' ? (
              <ul className="space-y-2.5" data-testid="faq-answer-list">
                {parsed.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 font-body text-[14px] md:text-[15px] leading-[1.7] text-ink-primary/85">
                    <span
                      className="mt-[10px] inline-block w-[6px] h-[6px] shrink-0 bg-brand-orange"
                      aria-hidden="true"
                    />
                    <span>{renderWithEmails(item, `li-${i}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="font-body text-[14px] md:text-[15px] leading-[1.7] text-ink-primary/85" data-testid="faq-answer-text">
                {renderWithEmails(parsed.text, 'p')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [entries, setEntries] = useState(null);
  const [err, setErr] = useState(null);
  const [openIndex, setOpenIndex] = useState(-1);
  // v3.15 — Self-fetch sections_meta.faq.eyebrow_size. Kept as an
  // isolated fetch so FAQSection remains prop-free (matches its
  // existing pattern) — Home.js is intentionally NOT modified.
  const [faqMeta, setFaqMeta] = useState({});
  const headerReveal = useReveal();

  useEffect(() => {
    let alive = true;
    api.get('/faq')
      .then((r) => { if (alive) setEntries(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (alive) setErr(e?.message || 'failed'); });
    // v3.15 — Independent fetch for FAQ section meta (only the
    // eyebrow_size key is consumed here; other keys stay untouched).
    api.get('/public/home')
      .then((r) => {
        if (!alive) return;
        const sm = r?.data?.settings?.sections_meta || {};
        setFaqMeta(sm.faq || {});
      })
      .catch(() => { /* keep default empty meta on failure */ });
    return () => { alive = false; };
  }, []);

  const toggle = (idx) => setOpenIndex((cur) => (cur === idx ? -1 : idx));

  return (
    <section
      id="faq"
      className="section-pad relative bg-bg-canvas"
      data-testid="section-faq"
    >
      <div className="container-x">
        <div
          ref={headerReveal.ref}
          className={`text-center ${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}
        >
          {/* v3.15 — FAQ eyebrow. TEXT stays hardcoded per scope; ONLY
              the size is now admin-configurable via
              sections_meta.faq.eyebrow_size. Defensive validation:
              integer in [16, 48]. */}
          {(() => {
            const _raw = faqMeta.eyebrow_size;
            const eyebrowSize = typeof _raw === 'number' && Number.isInteger(_raw) && _raw >= 16 && _raw <= 48 ? _raw : 26;
            return (
              <p className="eyebrow eyebrow-lg" data-testid="faq-eyebrow" style={{ '--eyebrow-size': `${eyebrowSize}px` }}>GOT QUESTIONS?</p>
            );
          })()}
          <h2
            className="mt-4 font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[36px] md:text-[40px] lg:text-[56px]"
            data-testid="faq-headline"
          >
            FREQUENTLY ASKED.
          </h2>
          <p
            className="mt-4 font-body text-[14px] md:text-[15px] text-ink-primary/70 max-w-[640px] mx-auto leading-relaxed"
            data-testid="faq-subhead"
          >
            Everything you need to know about SEICon &mdash; and how to get involved.
          </p>
        </div>

        <div className="mt-12 md:mt-14 mx-auto w-full max-w-[900px]">
          {err && entries === null && (
            <p
              className="font-body text-[14px] text-ink-primary/70 text-center py-6"
              data-testid="faq-error"
            >
              Unable to load FAQs. Please refresh the page.
            </p>
          )}

          {entries === null && !err && (
            <ul className="border-t border-ink-primary/15" data-testid="faq-loading">
              {[0, 1, 2, 3].map((i) => (
                <li key={i} className="border-b border-ink-primary/15 py-6 flex items-center justify-between gap-4">
                  <span className="h-4 bg-ink-primary/10 rounded w-2/3 animate-pulse" />
                  <span className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-ink-primary/10 animate-pulse" />
                </li>
              ))}
            </ul>
          )}

          {Array.isArray(entries) && entries.length > 0 && (
            <div className="border-t border-ink-primary/15" data-testid="faq-list">
              {entries.map((entry, idx) => (
                <FAQRow
                  key={entry.id || idx}
                  entry={entry}
                  index={idx}
                  isOpen={openIndex === idx}
                  onToggle={toggle}
                />
              ))}
            </div>
          )}

          {Array.isArray(entries) && entries.length === 0 && !err && (
            <p
              className="font-body text-[14px] text-ink-primary/70 text-center py-6"
              data-testid="faq-empty"
            >
              No FAQs available yet.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
